import { Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { Link, useParams } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import {
  Banknote,
  BarChart3,
  ChevronRight,
  FileText,
  Layers,
  MapPin,
  MapPinned,
  Store,
} from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { canUseMerchantPermission } from "@/modules/merchant/presentation/api/client";
import { hasShopFeature } from "@/modules/merchant/presentation/lib/has-shop-feature";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { PosSalesByCurrencyReportSection } from "@/modules/merchant/presentation/ui/PosSalesByCurrencyReportSection";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";

type QuickLink = {
  title: string;
  description: string;
  to:
    | "/app/shops/$shopId/pos/sales"
    | "/app/shops/$shopId/shifts/reports"
    | "/app/shops/$shopId/reports/branches"
    | "/app/shops/$shopId/reports/inventory"
    | "/app/shops/$shopId/reports/warehouse-locations"
    | "/app/shops/$shopId/reports/modules"
    | "/app/shops/$shopId/settlement";
  icon: LucideIcon;
  show: boolean;
};

/**
 * ລາຍງານ POS — P0/P1: ສະຫຼຸບຍອດຕາມສະກຸນ + ລິ້ງໄປລາຍງານໂມດູນ (ຕາມ feature flag)
 * @see docs/IMPLEMENT_MERCHANT_POS_REPORTS.md
 */
export function ShopReportsPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const { currentShop } = useMerchantShops(shopId);
  const flags = currentShop?.featureFlags;
  const verified = currentShop?.verifiedLevel ?? null;
  const canViewShiftReport =
    shopId != null &&
    canUseMerchantPermission(
      currentShop?.permissions ?? [],
      MerchantPermissions.shifts.manage,
      verified,
    );
  const canViewSettlementLink =
    shopId != null &&
    canUseMerchantPermission(
      currentShop?.permissions ?? [],
      MerchantPermissions.settlement.read,
      verified,
    );

  const quickLinks: QuickLink[] = [
    {
      title: "ປະຫວັດບິນ / ລາຍການຂາຍ",
      description: "ລາຍລະເອີດບິນ, void, refund",
      to: "/app/shops/$shopId/pos/sales",
      icon: FileText,
      show: true,
    },
    {
      title: "ລາຍງານກະ",
      description: "ສະຫຼຸບກ່ວາວັນ / ພະນັກງານ",
      to: "/app/shops/$shopId/shifts/reports",
      icon: BarChart3,
      show: hasShopFeature(flags, "shifts") && canViewShiftReport,
    },
    {
      title: "ຍອດຂາຍແຍກສາຂາ",
      description: "ເປຣຢີບຽບຍອດຕາມສາຂາ",
      to: "/app/shops/$shopId/reports/branches",
      icon: MapPin,
      show: hasShopFeature(flags, "multi_branch"),
    },
    {
      title: "ລາຍງານສາງ",
      description: "ສະຫຼຸບສຕ໋ອກ ແລະ ລິ້ງລາຍລະອຽດ",
      to: "/app/shops/$shopId/reports/inventory",
      icon: Store,
      show: hasShopFeature(flags, "inventory"),
    },
    {
      title: "ລາຍງານຕຳແໜ່ງສາງ",
      description: "ຍັງບໍ່ຈັດຊ່ອງ, ຊ່ອງວ່າງ, ຮັບເຂົ້າຕາມຊ່ອງ",
      to: "/app/shops/$shopId/reports/warehouse-locations",
      icon: MapPinned,
      show:
        hasShopFeature(flags, "inventory") &&
        hasShopFeature(flags, "warehouse_locations"),
    },
    {
      title: "ໂມດູນ PO / ຄຳຂໍ / ເຄສ",
      description: "ສະຫຼຸບສະຖານະຕາມຟີເຈີທີ່ເປີດ",
      to: "/app/shops/$shopId/reports/modules",
      icon: Layers,
      show: true,
    },
    {
      title: "ຊຳລະ / Settlement",
      description: "ກະແສເງິນ (ຕາມການເຊື່ອມລະບົບ)",
      to: "/app/shops/$shopId/settlement",
      icon: Banknote,
      show: hasShopFeature(flags, "settlement") && canViewSettlementLink,
    },
  ];

  return (
    <RequireMerchantPermission permission={MerchantPermissions.pos.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.reports(shopId)} />
      </Header>

      <Main>
        <div className="mb-6">
          <h1 className="font-heading font-semibold text-foreground text-xl">
            ລາຍງານ
          </h1>
          <p className="mt-1 text-muted-foreground text-sm">
            ສະຫຼຸບການຂາຍ ແລະ ລິ້ງໄປລາຍງານໂມດູນ — ຕາມ feature ທີ່ເປີດໃຫ້ຮ້ານ
          </p>
        </div>

        <div className="mb-8">
          <PosSalesByCurrencyReportSection shopId={shopId} />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="inline-flex items-center gap-2 text-base">
              <BarChart3 className="size-5 text-primary" />
              ລາຍງານ ແລະ ລິ້ງທີ່ກ່ຽວຂ້ອງ
            </CardTitle>
            <p className="text-muted-foreground text-sm">
              ລາຍການຂ້າງລຸ່ມຂຶ້ນກັບສິດ ແລະ featureຂອງຮ້ານ
            </p>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border rounded-lg border border-border">
              {quickLinks
                .filter((l) => l.show)
                .map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      params={{ shopId: shopId ?? "" }}
                      className="flex items-center gap-3 px-3 py-3 transition-colors hover:bg-muted/50"
                    >
                      <l.icon
                        className="size-5 shrink-0 text-muted-foreground"
                        aria-hidden
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-foreground text-sm">
                          {l.title}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {l.description}
                        </p>
                      </div>
                      <ChevronRight
                        className="size-4 shrink-0 text-muted-foreground"
                        aria-hidden
                      />
                    </Link>
                  </li>
                ))}
            </ul>
          </CardContent>
        </Card>
      </Main>
    </RequireMerchantPermission>
  );
}
